import React, { useRef, useState, useCallback, useEffect } from "react"
import {
  Send,
  Paperclip,
  X,
  Image,
  FileText,
  Users,
  ShieldCheck,
  ChevronDown,
  Reply,
  CornerUpLeft,
  Bot,
  Zap,
  Sparkles,
  MessageCircle,
} from "lucide-react"
import { useTranslation } from "react-i18next"
import { FOR_ADMIN_KEY, TO_CLIENT_KEY, STANDARD_THEME_IDS } from "./constants"
// AI implementation starts
import AiAssistPanel from "./AiAssistPanel"
import SmartReplyPopover from "./SmartReplyPopover"
// AI implementation end

const COMPOSE_HEIGHT_KEY = "chat-compose-height"
const MIN_HEIGHT = 0
const MAX_HEIGHT = 500
const DEFAULT_HEIGHT = 120
const COLLAPSE_THRESHOLD = 40

const isImageFile = (file) => file.type.startsWith("image/")

const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const SEND_MODES = [
  {
    key: "all",
    labelKey: "admin.send_to_all",
    fallback: "Всем",
    Icon: Users,
    activeClass: "bg-white text-slate-800 shadow-sm",
    inactiveClass: "text-slate-400 hover:text-slate-600",
  },
  {
    key: "for_admin",
    labelKey: "admin.send_to_admins",
    fallback: "Только админам",
    Icon: ShieldCheck,
    activeClass: "bg-white text-orange-600 shadow-sm",
    inactiveClass: "text-slate-400 hover:text-slate-600",
  },
  {
    key: TO_CLIENT_KEY,
    labelKey: "admin.send_to_client_telegram",
    fallback: "Клиенту в Telegram",
    Icon: MessageCircle,
    activeClass: "bg-white text-sky-600 shadow-sm",
    inactiveClass: "text-slate-400 hover:text-slate-600",
  },
]

// Режим «только админам» — для админов, «клиенту в Telegram» — для лидов, привязанных к боту.
const availableSendModes = ({ isAdmin, canSendToClient }) =>
  SEND_MODES.filter(
    ({ key }) => (key !== FOR_ADMIN_KEY || isAdmin) && (key !== TO_CLIENT_KEY || canSendToClient)
  )

const SendModeSelector = ({ value, onChange, modes }) => {
  const { t } = useTranslation()
  return (
    <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5 w-full sm:w-fit">
      {modes.map(({ key, labelKey, fallback, Icon, activeClass, inactiveClass }) => (
        <button
          key={key}
          type="button"
          onClick={() => onChange(key)}
          className={`flex items-center justify-center gap-1.5 flex-1 sm:flex-none px-3 py-2 rounded-md text-xs font-medium transition-all duration-150 min-h-[36px] ${
            value === key ? activeClass : inactiveClass
          }`}
        >
          <Icon className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="truncate">{t(labelKey, fallback)}</span>
        </button>
      ))}
    </div>
  )
}

const AttachedFileChip = ({ file, onRemove }) => {
  const isImg = isImageFile(file)
  const [previewUrl, setPreviewUrl] = useState(null)

  useEffect(() => {
    if (!isImg) return
    const url = URL.createObjectURL(file)
    setPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [file, isImg])

  return (
    <div className="relative flex-shrink-0 group">
      {isImg ? (
        <div className="w-20 h-20 rounded-xl overflow-hidden border border-slate-200 bg-slate-100">
          {previewUrl && (
            <img src={previewUrl} alt={file.name} className="w-full h-full object-cover" />
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2 px-3 py-2.5 bg-orange-50 border border-orange-200 rounded-xl max-w-[200px]">
          <FileText className="w-4 h-4 text-orange-500 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-xs font-medium text-slate-700 truncate">{file.name}</p>
            <p className="text-[10px] text-slate-400">{formatBytes(file.size)}</p>
          </div>
        </div>
      )}
      <button
        onClick={() => onRemove(file)}
        className="absolute -top-1.5 -right-1.5 w-6 h-6 bg-red-500 hover:bg-red-600 active:bg-red-700 rounded-full flex items-center justify-center shadow-sm sm:opacity-0 sm:group-hover:opacity-100 transition-opacity z-10"
        aria-label="Remove file"
      >
        <X className="w-3.5 h-3.5 text-white" />
      </button>
      {isImg && (
        <p className="text-[10px] text-slate-400 mt-0.5 text-center max-w-[80px] truncate">
          {file.name}
        </p>
      )}
    </div>
  )
}

const SendTooltip = ({
  show,
  isDepartmentSelected,
  hasDepartments,
  useDistribution,
  isThemeRequired,
  isThemeSelected,
  hasContent,
  t,
}) => {
  if (!show) return null
  return (
    <div className="absolute bottom-full right-0 mb-2 w-max max-w-[220px] bg-slate-800 text-white text-xs rounded-lg px-3 py-2.5 shadow-xl pointer-events-none z-20 opacity-0 group-hover/send:opacity-100 transition-opacity duration-150 hidden sm:block">
      <p className="font-semibold text-slate-200 mb-1.5">
        {t("admin.fill_required_fields", "Заполните обязательные поля:")}
      </p>
      <div className="flex flex-col gap-1">
        {useDistribution && hasDepartments && !isDepartmentSelected && (
          <p className="flex items-center gap-1.5 text-slate-300">
            <span className="w-1.5 h-1.5 rounded-full bg-orange-400 flex-shrink-0" />
            {t("admin.departament", "Департамент")}
          </p>
        )}
        {isThemeRequired && !isThemeSelected && (
          <p className="flex items-center gap-1.5 text-slate-300">
            <span className="w-1.5 h-1.5 rounded-full bg-orange-400 flex-shrink-0" />
            {t("admin.message_subject", "Тема сообщения")}
          </p>
        )}
        {!hasContent && (
          <p className="flex items-center gap-1.5 text-slate-300">
            <span className="w-1.5 h-1.5 rounded-full bg-orange-400 flex-shrink-0" />
            {t("admin.message_or_file", "Сообщение или файл")}
          </p>
        )}
      </div>
      <div className="absolute top-full right-5 border-4 border-transparent border-t-slate-800" />
    </div>
  )
}

const stripHtml = (html = "") =>
  html
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim()

const TemplatesDropdown = ({ onInsert }) => {
  const [open, setOpen] = useState(false)
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open || templates.length > 0) return
    setLoading(true)
    fetch("/book/ai/templates")
      .then((r) => r.json())
      .then((data) => setTemplates(Array.isArray(data) ? data : []))
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false))
  }, [open])

  return (
    <div className="relative">
      <button
        type="button"
        title="Quick templates"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 px-3 py-2.5 rounded-lg transition-colors min-h-[44px] ${
          open
            ? "bg-amber-100 text-amber-700"
            : "hover:bg-slate-100 active:bg-slate-200 text-slate-500 hover:text-slate-700"
        }`}
      >
        <Zap className="w-4 h-4" />
        <span className="text-sm">Templates</span>
      </button>

      {open && (
        <div className="absolute bottom-full left-0 z-30 w-72 bg-white border border-slate-200 rounded-xl shadow-xl p-2 mb-1 flex flex-col gap-1 max-h-64 overflow-y-auto">
          {loading && <p className="text-xs text-slate-400 text-center py-3">Loading…</p>}
          {!loading && templates.length === 0 && (
            <p className="text-xs text-slate-400 text-center py-3">No templates yet.</p>
          )}
          {templates.map((tpl) => (
            <button
              key={tpl.id}
              type="button"
              onClick={() => {
                onInsert(tpl.text)
                setOpen(false)
              }}
              className="text-left px-3 py-2 rounded-lg hover:bg-amber-50 hover:border-amber-200 border border-transparent transition-all group"
            >
              <p className="text-xs font-semibold text-slate-700 group-hover:text-amber-700">
                {tpl.label}
              </p>
              <p className="text-[11px] text-slate-400 mt-0.5 line-clamp-1">{tpl.text}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

const ReplyToBanner = ({ replyTo, onCancel, bundleId, onInsert, isAdmin }) => {
  const { t } = useTranslation()
  const [showAi, setShowAi] = useState(false)

  if (!replyTo) return null

  const preview = stripHtml(replyTo.text)
  const previewText = preview.length > 100 ? preview.slice(0, 100) + "…" : preview
  const hasImage = replyTo.files?.some((f) => {
    if (f.content_type) return f.content_type.startsWith("image/")
    const ext = f.file_name?.split(".").pop()?.toLowerCase()
    return ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "avif"].includes(ext)
  })
  const imageUrl = hasImage
    ? replyTo.files?.find((f) => {
        if (f.content_type) return f.content_type.startsWith("image/")
        const ext = f.file_name?.split(".").pop()?.toLowerCase()
        return ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "avif"].includes(ext)
      })?.url
    : null

  return (
    <div className="relative mx-3 mt-2 flex-shrink-0">
      {showAi && (
        <SmartReplyPopover
          replyTo={replyTo}
          bundleId={bundleId}
          onInsert={onInsert}
          onClose={() => setShowAi(false)}
        />
      )}
      <div className="flex items-stretch gap-0 rounded-xl overflow-hidden border border-blue-200 bg-blue-50/70">
        <div className="w-1 bg-blue-400 flex-shrink-0 rounded-l-xl" />
        <div className="flex items-center gap-2 px-3 py-2 flex-1 min-w-0">
          <CornerUpLeft className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-semibold text-blue-600 truncate">
              {replyTo.sender || t("admin.reply", "Ответ")}
            </p>
            {previewText ? (
              <p className="text-[12px] text-slate-500 truncate">{previewText}</p>
            ) : hasImage ? (
              <p className="text-[12px] text-slate-400 italic">
                {t("admin.image", "📎 Изображение")}
              </p>
            ) : (
              <p className="text-[12px] text-slate-400 italic">{t("admin.message", "Сообщение")}</p>
            )}
          </div>
          {imageUrl && (
            <img
              src={imageUrl}
              alt=""
              className="w-9 h-9 rounded-lg object-cover flex-shrink-0 border border-blue-100"
            />
          )}
        </div>
        {isAdmin && (
          <button
            type="button"
            onClick={() => setShowAi((v) => !v)}
            title="AI Reply"
            className={`px-2.5 flex items-center justify-center transition-colors flex-shrink-0 ${
              showAi
                ? "bg-blue-200 text-blue-700"
                : "hover:bg-blue-100 text-blue-400 hover:text-blue-600"
            }`}
          >
            <Sparkles className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          onClick={onCancel}
          className="px-2.5 flex items-center justify-center hover:bg-blue-100 transition-colors flex-shrink-0"
          title={t("admin.cancel", "Отмена")}
        >
          <X className="w-4 h-4 text-blue-400 hover:text-blue-600" />
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// DraftIndicator — маленький бейдж "черновик сохранён"
// ---------------------------------------------------------------------------
const DraftIndicator = ({ visible }) => {
  const [show, setShow] = useState(false)

  useEffect(() => {
    if (!visible) return
    setShow(true)
    const t = setTimeout(() => setShow(false), 2000)
    return () => clearTimeout(t)
  }, [visible])

  if (!show) return null

  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] text-slate-400 transition-opacity duration-500"
      style={{ opacity: show ? 1 : 0 }}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" />
      черновик сохранён
    </span>
  )
}

// ---------------------------------------------------------------------------
// Хук для изменения высоты textarea
// ---------------------------------------------------------------------------
const useTextareaResize = () => {
  const [height, setHeight] = useState(() => {
    try {
      const saved = localStorage.getItem(COMPOSE_HEIGHT_KEY)
      return saved ? parseFloat(saved) : DEFAULT_HEIGHT
    } catch {
      return DEFAULT_HEIGHT
    }
  })
  const isResizingRef = useRef(false)
  const lastHeightRef = useRef(height > COLLAPSE_THRESHOLD ? height : DEFAULT_HEIGHT)

  const startResize = useCallback(
    (e) => {
      e.preventDefault()
      isResizingRef.current = true
      const startY = e.clientY
      const startHeight = height

      const onMove = (e) => {
        if (!isResizingRef.current) return
        const delta = startY - e.clientY
        const newHeight = Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, startHeight + delta))
        setHeight(newHeight)
      }

      const onUp = () => {
        isResizingRef.current = false
        document.removeEventListener("mousemove", onMove)
        document.removeEventListener("mouseup", onUp)
        setHeight((h) => {
          if (h > COLLAPSE_THRESHOLD) {
            lastHeightRef.current = h
            try {
              localStorage.setItem(COMPOSE_HEIGHT_KEY, h)
            } catch {}
          }
          return h
        })
      }

      document.addEventListener("mousemove", onMove)
      document.addEventListener("mouseup", onUp)
    },
    [height]
  )

  const toggleCollapse = useCallback(() => {
    setHeight((h) => {
      if (h <= COLLAPSE_THRESHOLD) {
        const restored = lastHeightRef.current
        try {
          localStorage.setItem(COMPOSE_HEIGHT_KEY, restored)
        } catch {}
        return restored
      } else {
        lastHeightRef.current = h
        return 0
      }
    })
  }, [])

  const isCollapsed = height <= COLLAPSE_THRESHOLD

  return { height, startResize, isResizing: isResizingRef.current, toggleCollapse, isCollapsed }
}

// ---------------------------------------------------------------------------
// ComposeArea
// ---------------------------------------------------------------------------
const ComposeArea = ({
  message,
  setMessage,
  setDepartment,
  attachedFiles,
  setAttachedFiles,
  onSend,
  departments,
  department,
  filteredMessagethemes,
  setMessageTheme,
  messageTheme,
  setSendMode,
  activeSendMode,
  isAdmin,
  canSendToClient = false,
  replyTo,
  onCancelReply,
  useDistribution,
  messageSubjectIsRequired,
  onDraftChange,
  autofillStandardThemeIds,
  autofillStandardThemesScope,
  bundleId,
}) => {
  const { t } = useTranslation()
  const fileInputRef = useRef(null)
  const textareaRef = useRef(null)
  const [isDragging, setIsDragging] = useState(false)
  // счётчик сохранений — используется для мигания индикатора
  const [draftSaveCount, setDraftSaveCount] = useState(0)
  const draftDebounceRef = useRef(null)
  const files = attachedFiles || []

  const {
    height: textareaHeight,
    startResize,
    isResizing,
    toggleCollapse,
    isCollapsed,
  } = useTextareaResize()

  useEffect(() => {
    if (replyTo && textareaRef.current && !isCollapsed) {
      textareaRef.current.focus()
    }
  }, [replyTo, isCollapsed])

  // ---------------------------------------------------------------------------
  // Вызов onDraftChange с debounce 500ms
  // Принимает актуальные значения явно, чтобы не захватывать устаревший closure
  // ---------------------------------------------------------------------------
  const triggerDraftSave = useCallback(
    (msg, dept, theme) => {
      if (!onDraftChange) return
      if (draftDebounceRef.current) clearTimeout(draftDebounceRef.current)
      draftDebounceRef.current = setTimeout(() => {
        onDraftChange({ message: msg, department: dept, messageTheme: theme })
        setDraftSaveCount((c) => c + 1)
      }, 500)
    },
    [onDraftChange]
  )

  // очистка debounce при анмаунте
  useEffect(() => {
    return () => {
      if (draftDebounceRef.current) clearTimeout(draftDebounceRef.current)
    }
  }, [])

  const assignUid = (file) => {
    if (!file._uid) {
      file._uid = `${file.name}-${file.size}-${Date.now()}-${Math.random()}`
    }
    return file
  }

  const addFiles = useCallback(
    (newFiles) => {
      const arr = Array.from(newFiles).map(assignUid)
      setAttachedFiles((prev) => [...(prev || []), ...arr])
    },
    [setAttachedFiles]
  )

  useEffect(() => {
    if (!messageTheme) return
    const foundTheme = filteredMessagethemes.find(
      (item) => String(item.id) === String(messageTheme)
    )
    if (!foundTheme?.description) return

    const isStandardTheme = Object.values(STANDARD_THEME_IDS).includes(String(messageTheme))

    if (isStandardTheme) {
      const allowedThemes = autofillStandardThemeIds || []
      if (!allowedThemes.includes(String(messageTheme))) return

      const scope = autofillStandardThemesScope || "all"
      const scopeAllows =
        scope === "all" || (scope === "admin" && isAdmin) || (scope === "partner" && !isAdmin)

      if (!scopeAllows) return
    }

    setMessage(foundTheme.description)
  }, [messageTheme])

  const removeFile = useCallback(
    (fileToRemove) => {
      setAttachedFiles((prev) => prev.filter((f) => f._uid !== fileToRemove._uid))
    },
    [setAttachedFiles]
  )

  const handlePaste = useCallback(
    (e) => {
      const items = e.clipboardData?.items
      if (!items) return
      const pastedFiles = []
      for (let i = 0; i < items.length; i++) {
        const item = items[i]
        if (item.kind === "file") {
          const file = item.getAsFile()
          if (file?.type.startsWith("image/")) pastedFiles.push(file)
        }
      }
      if (pastedFiles.length > 0) {
        e.preventDefault()
        addFiles(pastedFiles)
      }
    },
    [addFiles]
  )

  const handleFileChange = (e) => {
    if (e.target.files?.length) addFiles(e.target.files)
    e.target.value = ""
  }

  // обёртки для контролируемых полей — сохраняют черновик при каждом изменении
  const handleMessageChange = (e) => {
    const val = e.target.value
    setMessage(val)
    triggerDraftSave(val, department, messageTheme)
  }

  const handleDepartmentChange = (e) => {
    const val = e.target.value
    if (setDepartment) setDepartment(val)
    triggerDraftSave(message, val, messageTheme)
  }

  const handleThemeChange = (e) => {
    const val = e.target.value
    if (setMessageTheme) setMessageTheme(val)
    triggerDraftSave(message, department, val)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setIsDragging(true)
  }
  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setIsDragging(false)
  }
  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragging(false)
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files)
  }

  const hasDepartments = departments?.length > 0
  const isDepartmentRequired = Boolean(useDistribution) && hasDepartments
  const isDepartmentSelected = Boolean(department)
  const showDepartmentSelect = Boolean(useDistribution) && hasDepartments
  const hasThemes = filteredMessagethemes.length > 0
  const showThemeSelect = hasThemes && (!useDistribution || isDepartmentSelected)

  const isThemeRequired = showThemeSelect

  const isThemeSelected = Boolean(messageTheme)
  const hasContent = Boolean(message.trim() || files.length > 0)

  const canSend = replyTo
    ? hasContent
    : hasContent &&
      (!isDepartmentRequired || isDepartmentSelected) &&
      (!isThemeRequired || isThemeSelected)

  const imageFiles = files.filter(isImageFile)
  const otherFiles = files.filter((f) => !isImageFile(f))

  return (
    <div
      className={`relative bg-white border-t border-slate-200 flex flex-col transition-colors ${
        isResizing ? "select-none" : ""
      } ${isDragging ? "bg-orange-50/30" : ""}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDragging && (
        <div className="absolute inset-0 border-2 border-dashed border-orange-400 bg-orange-50/60 z-10 flex items-center justify-center pointer-events-none">
          <p className="text-orange-500 font-semibold text-sm">{t("admin.drop_your_files_here")}</p>
        </div>
      )}

      {/* Ручка ресайза */}
      <div
        className="flex items-center justify-center h-4 cursor-row-resize flex-shrink-0 group border-b border-slate-100"
        onMouseDown={startResize}
        onClick={toggleCollapse}
        title={isCollapsed ? "Развернуть" : "Свернуть / изменить высоту"}
      >
        <div className="w-10 h-1 rounded-full bg-slate-200 group-hover:bg-blue-400 transition-colors" />
      </div>

      {!isCollapsed && (
        <>
          {replyTo && (
            <ReplyToBanner
              replyTo={replyTo}
              onCancel={onCancelReply}
              bundleId={bundleId}
              onInsert={(text) => setMessage(text)}
              isAdmin={isAdmin}
            />
          )}

          {/* Блок выбора департамента */}
          {showDepartmentSelect && (
            <div className="flex flex-col gap-2 px-3 pt-2 sm:px-4">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide px-0.5">
                  {t("admin.departament")}
                </label>
                <div className="relative">
                  <select
                    value={department || ""}
                    onChange={handleDepartmentChange}
                    className="w-full px-3 py-3 pr-9 bg-white border border-slate-300 rounded-lg text-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer"
                  >
                    <option value="" disabled>{`${t("admin.select_department")}...`}</option>
                    {departments?.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>
              </div>

              {showThemeSelect && (
                <div className="flex flex-col gap-1">
                  <label
                    className={`text-xs font-semibold uppercase tracking-wide px-0.5 transition-colors ${
                      isDepartmentSelected ? "text-slate-500" : "text-slate-300"
                    }`}
                  >
                    {t("admin.message_subject")}
                  </label>
                  <div className="relative">
                    <select
                      value={messageTheme || ""}
                      onChange={handleThemeChange}
                      disabled={!isDepartmentSelected}
                      className={`w-full px-3 py-3 pr-9 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none transition-all ${
                        isDepartmentSelected
                          ? "bg-white border-slate-300 text-slate-700 cursor-pointer"
                          : "bg-slate-50 border-slate-200 text-slate-300 cursor-not-allowed"
                      }`}
                    >
                      <option value="" disabled>
                        {isDepartmentSelected
                          ? `${t("admin.select_message_theme")}...`
                          : t("admin.first_select_department")}
                      </option>
                      {isDepartmentSelected &&
                        filteredMessagethemes.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.title}
                          </option>
                        ))}
                    </select>
                    {isDepartmentSelected ? (
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    ) : (
                      <svg
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300 pointer-events-none"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        />
                      </svg>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Темы без департамента */}
          {!showDepartmentSelect && showThemeSelect && (
            <div className="flex flex-col gap-1 px-3 pt-2 sm:px-4">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide px-0.5">
                {t("admin.message_subject")}
              </label>
              <div className="relative">
                <select
                  value={messageTheme || ""}
                  onChange={handleThemeChange}
                  className="w-full px-3 py-3 pr-9 bg-white border border-slate-300 rounded-lg text-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer"
                >
                  <option value="" disabled>{`${t("admin.select_message_theme")}...`}</option>
                  {filteredMessagethemes.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.title}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
            </div>
          )}

          {/* Textarea */}
          <div className="px-3 pt-2 sm:px-4">
            <textarea
              ref={textareaRef}
              value={message}
              onChange={handleMessageChange}
              onPaste={handlePaste}
              placeholder={
                replyTo ? t("admin.reply_placeholder", "Напишите ответ...") : "Compose an epic..."
              }
              style={{ height: Math.max(textareaHeight, 60) }}
              className="w-full px-3 py-3 border border-slate-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-[15px] leading-relaxed"
            />
          </div>

          {/* Прикреплённые файлы */}
          {files.length > 0 && (
            <div className="flex flex-col gap-2 px-3 sm:px-4">
              {imageFiles.length > 0 && (
                <div>
                  <p className="text-[11px] text-slate-400 font-medium uppercase tracking-wide mb-1.5 flex items-center gap-1">
                    <Image className="w-3 h-3" />
                    {t("admin.picture")} ({imageFiles.length})
                  </p>
                  <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                    {imageFiles.map((file) => (
                      <AttachedFileChip key={file._uid} file={file} onRemove={removeFile} />
                    ))}
                  </div>
                </div>
              )}
              {otherFiles.length > 0 && (
                <div>
                  <p className="text-[11px] text-slate-400 font-medium uppercase tracking-wide mb-1.5 flex items-center gap-1">
                    <FileText className="w-3 h-3" />
                    {t("admin.files")} ({otherFiles.length})
                  </p>
                  <div className="flex flex-col gap-1.5 sm:flex-row sm:flex-wrap">
                    {otherFiles.map((file) => (
                      <AttachedFileChip key={file._uid} file={file} onRemove={removeFile} />
                    ))}
                  </div>
                </div>
              )}
              {files.length > 1 && (
                <button
                  onClick={() => setAttachedFiles([])}
                  className="self-start text-xs text-red-400 hover:text-red-600 active:text-red-700 transition-colors py-1"
                >
                  {t("admin.delete_all_files")}
                </button>
              )}
            </div>
          )}

          {/* Bottom bar */}
          <div className="flex flex-col gap-2 px-3 pb-3 sm:px-4 sm:pb-4 pt-2">
            {(() => {
              const modes = availableSendModes({ isAdmin, canSendToClient })
              return (
                modes.length > 1 && (
                  <SendModeSelector value={activeSendMode} onChange={setSendMode} modes={modes} />
                )
              )
            })()}

            <div className="relative flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  multiple
                  type="file"
                  className="hidden"
                  onChange={handleFileChange}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg hover:bg-slate-100 active:bg-slate-200 transition-colors text-slate-500 hover:text-slate-700 min-h-[44px]"
                  aria-label={t("admin.add_file")}
                >
                  <Paperclip className="w-4 h-4" />
                  <span className="text-sm">{t("admin.add_file")}</span>
                </button>
                {isAdmin && <TemplatesDropdown onInsert={(text) => setMessage(text)} />}
                {isAdmin && (
                  <AiAssistPanel bundleId={bundleId} onInsert={(text) => setMessage(text)} />
                )}

                {files.length > 0 && (
                  <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
                    {files.length}{" "}
                    {files.length === 1
                      ? t("file.singular_lower")
                      : files.length < 5
                        ? t("file.nominative_plural_lower")
                        : t("admin.genitive_plural_lower")}
                  </span>
                )}

                {/* Индикатор сохранения черновика */}
                <DraftIndicator visible={draftSaveCount > 0} key={draftSaveCount} />
              </div>

              <div className="relative flex-shrink-0 group/send">
                <button
                  onClick={canSend ? onSend : undefined}
                  aria-disabled={!canSend}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-lg transition-all font-medium text-sm min-h-[44px] ${
                    canSend
                      ? replyTo
                        ? "bg-blue-500 hover:bg-blue-600 active:bg-blue-700 text-white shadow-sm cursor-pointer"
                        : "bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white shadow-sm cursor-pointer"
                      : "bg-slate-200 text-slate-400 cursor-not-allowed"
                  }`}
                >
                  {replyTo && canSend ? (
                    <>
                      <Reply className="w-4 h-4" />
                      <span>{t("admin.reply_send", "Ответить")}</span>
                    </>
                  ) : (
                    <>
                      <span>
                        {activeSendMode === FOR_ADMIN_KEY && isAdmin
                          ? t("admin.send_to_admins")
                          : activeSendMode === TO_CLIENT_KEY && canSendToClient
                            ? t("admin.send_to_client_telegram", "Клиенту в Telegram")
                            : t("admin.send")}
                      </span>
                      <Send className="w-4 h-4" />
                    </>
                  )}
                </button>

                <SendTooltip
                  show={!canSend}
                  isDepartmentSelected={isDepartmentSelected}
                  hasDepartments={hasDepartments}
                  useDistribution={useDistribution}
                  isThemeRequired={isThemeRequired}
                  isThemeSelected={isThemeSelected}
                  hasContent={hasContent}
                  t={t}
                />
              </div>
            </div>

            <p className="text-[11px] text-slate-300 text-center hidden sm:block">
              {t("file.drag_files_here_attach")}
            </p>
          </div>
        </>
      )}
    </div>
  )
}

export default ComposeArea
